import React, { useState, useEffect } from "react";
import { PaletteColor, PaletteColorHex } from "../types/PaletteColor.ts";
import {
  RecurrenceRule,
  RecurrenceType,
  DueDateType,
  Task,
  CustomColumnValue,
} from "../types/types.ts";
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Event as EventIcon,
  Notifications as NotificationsIcon,
  Alarm as AlarmIcon,
} from "@mui/icons-material";
import Switch from "@mui/material/Switch";

interface RecurrenceDialogProps {
  initialRule: RecurrenceRule | undefined;
  onClose: () => void;
  onSave: (ruleData: Omit<RecurrenceRule, "recurringRuleId">) => void;
  color: PaletteColor;
  currentTask?: Task; // 現在編集中のタスク（contents生成用）
  customColumnValues?: CustomColumnValue[]; // カスタム項目の値（contents生成用）
  onOpenManagement?: () => void; // 繰り返し作成一覧を開く
  isMobile?: boolean; // スマホ版かどうか
  isInline?: boolean; // モーダルラッパーなしでフォームのみ描画
  hideCancel?: boolean; // キャンセルボタンを非表示にする
  footnote?: React.ReactNode; // フォーム最上部に表示する脚注（説明文）
}

// 曜日と日付の選択肢
const weekdays = ["日", "月", "火", "水", "木", "金", "土"];
// 1-31日 + 最終日(32)
const monthDays = [...Array(31).keys()].map((i) => i + 1);
monthDays.push(32);

export function RecurrenceDialog({
  initialRule,
  onClose,
  onSave,
  color,
  currentTask,
  customColumnValues,
  onOpenManagement,
  isMobile = false,
  isInline = false,
  hideCancel = false,
  footnote,
}: RecurrenceDialogProps) {
  // デフォルト値（initialRule が undefined の場合）
  const defaultRule: Omit<RecurrenceRule, "recurringRuleId"> = {
    recurrenceType: "Daily",
    recurrenceValue: undefined,
    periodStartDate: "",
    periodEndDate: "",
    dueDateType: "None",
    dueDateRelativeDays: 1,
    dueDateNextDayOfMonth: 1,
    isDueDateDuration: false,
    dueDateRelativeDaysEnd: 3,
    dueDateNextDayOfMonthEnd: 1,
    notificationEnabled: false,
    reminderType: "None",
    reminderRelativeDays: 0, // 当日をデフォルトに
    reminderNextDayOfMonth: 1,
  };

  const effectiveInitialRule = initialRule || defaultRule;
  const initialType =
    effectiveInitialRule.recurrenceType === "None" ||
    !effectiveInitialRule.recurrenceType
      ? "Daily"
      : effectiveInitialRule.recurrenceType;

  // 1. 繰り返しタイプ
  const [type, setType] = useState<RecurrenceType>(initialType);
  const [value, setValue] = useState<number | undefined>(
    effectiveInitialRule.recurrenceValue
  );
  const [intervalMonths, setIntervalMonths] = useState<number>(
    effectiveInitialRule.intervalMonths ?? 1
  );
  const [recurrenceMonth, setRecurrenceMonth] = useState<number>(
    effectiveInitialRule.recurrenceMonth ?? 1
  );

  // 3. 繰り返し期間
  const [periodStart, setPeriodStart] = useState(
    effectiveInitialRule.periodStartDate || ""
  );
  const [periodEnd, setPeriodEnd] = useState(
    effectiveInitialRule.periodEndDate || ""
  );

  // 4. 期限設定
  const [dueDateType, setDueDateType] = useState<DueDateType>(
    effectiveInitialRule.dueDateType
  );
  const [relativeDays, setRelativeDays] = useState(
    effectiveInitialRule.dueDateRelativeDays || 1
  );
  const [nextDayOfMonth, setNextDayOfMonth] = useState(
    (effectiveInitialRule as any).dueDateNextDayOfMonth ?? 1
  );
  const [isDueDateDuration, setIsDueDateDuration] = useState(
    effectiveInitialRule.isDueDateDuration || false
  );
  const [relativeDaysEnd, setRelativeDaysEnd] = useState(
    effectiveInitialRule.dueDateRelativeDaysEnd || 3
  );
  const [nextDayOfMonthEnd, setNextDayOfMonthEnd] = useState(
    effectiveInitialRule.dueDateNextDayOfMonthEnd ?? 1
  );

  // 5. 通知設定
  const [notification, setNotification] = useState(
    effectiveInitialRule.notificationEnabled
  );

  // 6. リマインダー設定
  const [reminderType, setReminderType] = useState<DueDateType>(
    effectiveInitialRule.reminderType || "None"
  );
  const [reminderRelativeDays, setReminderRelativeDays] = useState(
    effectiveInitialRule.reminderRelativeDays ?? 0
  );
  const [reminderNextDayOfMonth, setReminderNextDayOfMonth] = useState(
    effectiveInitialRule.reminderNextDayOfMonth ?? 1
  );

  const [isPeriodOptionsOpen, setIsPeriodOptionsOpen] = useState(false);

  // サブタスクを再帰的に取得する関数
  const extractChildTasks = (task: Task): any[] => {
    if (!task.childTasks || task.childTasks.length === 0) {
      return [];
    }

    const ts = Date.now();
    return task.childTasks.map((child, i) => ({
      id: child.id || `temp-${ts}-${i}`,
      title: child.title,
      memo: child.memo,
      priority: child.priority,
      notification: child.notification,
      assignees:
        child.assignees?.map((a) => ({ id: a.id, name: a.name })) || [],
      requesters:
        child.requesters?.map((r) => ({ id: r.id, name: r.name })) || [],
      tags:
        child.tags?.map((t) => ({ id: t.id, name: t.name, color: t.color })) ||
        [],
      childTasks: extractChildTasks(child), // 再帰的にサブタスクを取得
    }));
  };

  const handleSaveClick = () => {
    // currentTaskが必須
    if (!currentTask) {
      alert("エラー: タスク情報が取得できませんでした。");
      return;
    }

    let finalValue: number | undefined = undefined;
    if (type === "Weekly") {
      finalValue = value ?? 1;
    } else if (type === "Monthly" || type === "Yearly") {
      finalValue = value ?? 1;
    }

    // タスク内容をJSON形式で保存（期限・リマインダーは除く、サブタスクを含む）
    const taskTemplate = {
      id: currentTask.id, // サブタスクのカスタム項目値を DB から補完するためのひも付け用
      title: currentTask.title,
      memo: currentTask.memo,
      priority: currentTask.priority,
      notification: currentTask.notification,
      assignees:
        currentTask.assignees?.map((a) => ({ id: a.id, name: a.name })) || [],
      requesters:
        currentTask.requesters?.map((r) => ({ id: r.id, name: r.name })) || [],
      tags:
        currentTask.tags?.map((t) => ({
          id: t.id,
          name: t.name,
          color: t.color,
        })) || [],
      childTasks: extractChildTasks(currentTask), // サブタスク情報を追加
      customColumnValues: customColumnValues
        ?.filter((v) => v.value != null && v.value !== "")
        .map((v) => ({ customColumnId: v.customColumnId, value: v.value })) ?? [],
    };
    const contents = JSON.stringify(taskTemplate);

    onSave({
      recurrenceType: type,
      recurrenceValue: finalValue,
      intervalMonths: type === "Monthly" ? intervalMonths : 1,
      recurrenceMonth: type === "Yearly" ? recurrenceMonth : undefined,
      periodStartDate: periodStart || undefined,
      periodEndDate: periodEnd || undefined,
      dueDateType: dueDateType,
      dueDateRelativeDays:
        dueDateType === "RelativeDays" ? Number(relativeDays) || 1 : undefined,
      dueDateNextDayOfMonth:
        dueDateType === "NextDayOfMonth" ? Number(nextDayOfMonth) : undefined,
      isDueDateDuration: isDueDateDuration,
      dueDateRelativeDaysEnd:
        dueDateType === "RelativeDays" && isDueDateDuration
          ? Number(relativeDaysEnd) || 1
          : undefined,
      dueDateNextDayOfMonthEnd:
        dueDateType === "NextDayOfMonth" && isDueDateDuration
          ? Number(nextDayOfMonthEnd)
          : undefined,
      notificationEnabled: notification,
      reminderType: reminderType,
      reminderRelativeDays:
        reminderType === "RelativeDays"
          ? Number(reminderRelativeDays)
          : undefined,
      reminderNextDayOfMonth:
        reminderType === "NextDayOfMonth"
          ? Number(reminderNextDayOfMonth)
          : undefined,
      contents: contents,
    });
  };

  // type変更時にvalueをリセット
  useEffect(() => {
    if (type === "Weekly" && (value === undefined || value > 6)) {
      setValue(1); // 月曜
    } else if ((type === "Monthly" || type === "Yearly") && (value === undefined || value > 32)) {
      setValue(1); // 1日
    } else if (type === "Daily") {
      setValue(undefined);
    }
  }, [type, value]);

  const colorHex = PaletteColorHex[color] || "#000000";

  const formContent = (
    <>
      {/* コンテンツ (スクロール領域) */}
      <div className={isInline ? "p-4 space-y-6" : "overflow-y-auto p-6 space-y-6"}>
          {/* 脚注（説明文）: 呼び出し元から渡された場合のみ最上部に表示 */}
          {footnote}
          {/* 1. 繰り返しタイプ */}
          <Section title="繰り返し作成の頻度">
            <RadioOption
              label="毎日"
              name="recurrenceType"
              checked={type === "Daily"}
              onChange={() => setType("Daily")}
              color={colorHex}
            />
            <RadioOption
              label="毎週"
              name="recurrenceType"
              checked={type === "Weekly"}
              onChange={() => setType("Weekly")}
              color={colorHex}
            >
              <select
                value={value ?? 1}
                onChange={(e) => setValue(Number(e.target.value))}
                disabled={type !== "Weekly"}
                className="p-1 border rounded text-sm bg-white disabled:bg-gray-100"
              >
                {weekdays.map((day, index) => (
                  <option key={index} value={index}>
                    {day}曜日
                  </option>
                ))}
              </select>
            </RadioOption>
            <RadioOption
              label=""
              name="recurrenceType"
              checked={type === "Monthly"}
              onChange={() => setType("Monthly")}
              color={colorHex}
            >
              <select
                value={intervalMonths}
                onChange={(e) => setIntervalMonths(Number(e.target.value))}
                disabled={type !== "Monthly"}
                className="p-1 border rounded text-sm bg-white disabled:bg-gray-100"
              >
                {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                  <option key={m} value={m}>
                    {m === 1 ? "毎月" : `${m}ヶ月ごと`}
                  </option>
                ))}
              </select>
              <select
                value={value ?? 1}
                onChange={(e) => setValue(Number(e.target.value))}
                disabled={type !== "Monthly"}
                className="p-1 border rounded text-sm bg-white disabled:bg-gray-100"
              >
                {monthDays.map((day) => (
                  <option key={day} value={day}>
                    {day === 32 ? "最終日" : `${day}日`}
                  </option>
                ))}
              </select>
            </RadioOption>
            <RadioOption
              label="毎年"
              name="recurrenceType"
              checked={type === "Yearly"}
              onChange={() => setType("Yearly")}
              color={colorHex}
            >
              <select
                value={recurrenceMonth}
                onChange={(e) => setRecurrenceMonth(Number(e.target.value))}
                disabled={type !== "Yearly"}
                className="p-1 border rounded text-sm bg-white disabled:bg-gray-100"
              >
                {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                  <option key={m} value={m}>
                    {m}月
                  </option>
                ))}
              </select>
              <select
                value={value ?? 1}
                onChange={(e) => setValue(Number(e.target.value))}
                disabled={type !== "Yearly"}
                className="p-1 border rounded text-sm bg-white disabled:bg-gray-100"
              >
                {monthDays.map((day) => (
                  <option key={day} value={day}>
                    {day === 32 ? "最終日" : `${day}日`}
                  </option>
                ))}
              </select>
            </RadioOption>
          </Section>

          {/* 3. 繰り返し期間 */}
          <button
            onClick={() => setIsPeriodOptionsOpen(!isPeriodOptionsOpen)}
            className="flex items-center text-sm font-medium w-full"
            style={{ color: colorHex }}
          >
            {isPeriodOptionsOpen ? (
              <ExpandLessIcon fontSize="small" className="mr-1" />
            ) : (
              <ExpandMoreIcon fontSize="small" className="mr-1" />
            )}
            {isPeriodOptionsOpen ? "オプションを閉じる" : "オプション設定"}
          </button>

          {isPeriodOptionsOpen && (
            <Section title="繰り返し作成の期間">
              <div className="space-y-3 pt-2">
                <div className="flex items-center space-x-2">
                  <input
                    type="date"
                    value={periodStart || ""}
                    onChange={(e) => setPeriodStart(e.target.value)}
                    className="w-full p-2 border rounded text-sm"
                  />
                  <span>～</span>
                  <input
                    type="date"
                    value={periodEnd || ""}
                    onChange={(e) => setPeriodEnd(e.target.value)}
                    className="w-full p-2 border rounded text-sm"
                  />
                </div>
                <div className="flex justify-between items-center pt-1">
                  <p className="text-xs text-gray-500 flex-1 pr-4">
                    設定した日付の期間内でのみ繰り返し自動作成が行われます
                  </p>
                  <button
                    onClick={() => {
                      setPeriodStart("");
                      setPeriodEnd("");
                    }}
                    className="text-sm text-gray-500 hover:underline flex-shrink-0"
                  >
                    クリア
                  </button>
                </div>
              </div>
            </Section>
          )}

          {/* 4 & 5. 期限と通知 */}
          <div className="border-t pt-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              自動作成タスクの項目設定
            </h3>

            {/* 4. 期限の自動設定 */}
            <Section
              title={
                <span className="flex items-center">
                  <EventIcon fontSize="small" className="mr-2" />
                  期限の設定
                </span>
              }
            >
              {/* 期限なし */}
              <RadioOption
                label="期限なし (元のタスクの期限は無視)"
                name="dueDateType"
                checked={dueDateType === "None"}
                onChange={() => setDueDateType("None")}
                color={colorHex}
              />

              {/* 作成日から N 日後 */}
              <RadioOption
                label={isDueDateDuration ? "作成日の" : "作成日から"}
                name="dueDateType"
                checked={dueDateType === "RelativeDays"}
                onChange={() => setDueDateType("RelativeDays")}
                color={colorHex}
              >
                <input
                  type="number"
                  min="1"
                  value={relativeDays}
                  onChange={(e) =>
                    setRelativeDays(parseInt(e.target.value, 10) || 1)
                  }
                  onFocus={(e) => e.target.select()}
                  disabled={dueDateType !== "RelativeDays"}
                  className="w-16 p-1 border rounded text-sm text-center disabled:bg-gray-100"
                />
                <span className="text-sm">日後</span>

                {/* 期間がONの場合の終了日入力 */}
                {isDueDateDuration && (
                  <>
                    <span className="text-sm">～</span>
                    <input
                      type="number"
                      min={relativeDays}
                      value={relativeDaysEnd}
                      onChange={(e) =>
                        setRelativeDaysEnd(
                          parseInt(e.target.value, 10) || relativeDays
                        )
                      }
                      onFocus={(e) => e.target.select()}
                      disabled={dueDateType !== "RelativeDays"}
                      className="w-16 p-1 border rounded text-sm text-center disabled:bg-gray-100"
                    />
                    <span className="text-sm">日後</span>
                  </>
                )}
              </RadioOption>

              {/* 次の N 日 */}
              <RadioOption
                label={isDueDateDuration ? "次の" : "次の"}
                name="dueDateType"
                checked={dueDateType === "NextDayOfMonth"}
                onChange={() => setDueDateType("NextDayOfMonth")}
                color={colorHex}
              >
                <select
                  value={nextDayOfMonth}
                  onChange={(e) => setNextDayOfMonth(Number(e.target.value))}
                  disabled={dueDateType !== "NextDayOfMonth"}
                  className="p-1 border rounded text-sm bg-white disabled:bg-gray-100"
                >
                  {monthDays.map((day) => (
                    <option key={day} value={day}>
                      {day === 32 ? "最終日" : `${day}日`}
                    </option>
                  ))}
                </select>

                {/* 期間がONの場合の終了日入力 */}
                {isDueDateDuration && (
                  <>
                    <span className="text-sm">～</span>
                    <select
                      value={nextDayOfMonthEnd}
                      onChange={(e) =>
                        setNextDayOfMonthEnd(Number(e.target.value))
                      }
                      disabled={dueDateType !== "NextDayOfMonth"}
                      className="p-1 border rounded text-sm bg-white disabled:bg-gray-100"
                    >
                      {monthDays.map((day) => (
                        <option key={day} value={day}>
                          {day === 32 ? "最終日" : `${day}日`}
                        </option>
                      ))}
                    </select>
                  </>
                )}
              </RadioOption>
              {/* 期間設定スイッチ */}
              <label className="flex items-center justify-end cursor-pointer pb-2">
                <Switch
                  checked={isDueDateDuration}
                  onChange={(e) => setIsDueDateDuration(e.target.checked)}
                  sx={{
                    "&.Mui-checked": {
                      color: colorHex,
                      "& + .MuiSwitch-track": { backgroundColor: colorHex },
                    },
                  }}
                />
                <span className="text-sm mr-2">期間を設定する</span>
              </label>
            </Section>

            {/* 6. リマインダー設定 */}
            <Section
              title={
                <span className="flex items-center">
                  <AlarmIcon fontSize="small" className="mr-2" />
                  リマインダーの設定
                </span>
              }
            >
              <RadioOption
                label="なし"
                name="reminderType"
                checked={reminderType === "None"}
                onChange={() => setReminderType("None")}
                color={colorHex}
              />
              <RadioOption
                label="作成日から"
                name="reminderType"
                checked={reminderType === "RelativeDays"}
                onChange={() => setReminderType("RelativeDays")}
                color={colorHex}
              >
                <input
                  type="number"
                  min="0"
                  value={reminderRelativeDays}
                  onChange={(e) =>
                    setReminderRelativeDays(parseInt(e.target.value, 10) || 0)
                  }
                  onFocus={(e) => e.target.select()}
                  disabled={reminderType !== "RelativeDays"}
                  className="w-16 p-1 border rounded text-sm text-center disabled:bg-gray-100"
                />
                <span className="text-sm">日後の 8:00</span>
              </RadioOption>
              <RadioOption
                label="次の"
                name="reminderType"
                checked={reminderType === "NextDayOfMonth"}
                onChange={() => setReminderType("NextDayOfMonth")}
                color={colorHex}
              >
                <select
                  value={reminderNextDayOfMonth}
                  onChange={(e) =>
                    setReminderNextDayOfMonth(Number(e.target.value))
                  }
                  disabled={reminderType !== "NextDayOfMonth"}
                  className="p-1 border rounded text-sm bg-white disabled:bg-gray-100"
                >
                  {monthDays.map((day) => (
                    <option key={day} value={day}>
                      {day === 32 ? "最終日" : `${day}日`}
                    </option>
                  ))}
                </select>
                <span className="text-sm">の 8:00</span>
              </RadioOption>
              <p className="text-xs text-gray-500 ml-8">
                ※親タスクにのみ設定されます
              </p>
            </Section>

            {/* 5. 通知設定 */}
            <Section
              title={
                <span className="flex items-center">
                  <NotificationsIcon fontSize="small" className="mr-2" />
                  通知設定
                </span>
              }
            >
              <label className="flex items-center cursor-pointer">
                <Switch
                  checked={notification}
                  onChange={(e) => setNotification(e.target.checked)}
                  sx={{
                    "&.Mui-checked": {
                      color: colorHex,
                      "& + .MuiSwitch-track": { backgroundColor: colorHex },
                    },
                  }}
                />
                <span className="text-sm">作成時に通知する</span>
              </label>
            </Section>
          </div>
        </div>

      {/* フッター */}
      <div
        className={`flex justify-between items-center space-x-3 p-4 border-t ${
          isInline
            ? "sticky bottom-0 bg-white"
            : `bg-gray-50 sticky bottom-0 ${isMobile ? "" : "rounded-b-lg"}`
        }`}
      >
        <div>
          {!isInline && onOpenManagement && (
            <button
              onClick={() => {
                onOpenManagement();
                onClose();
              }}
              className="px-4 py-2 border rounded-md text-sm font-medium hover:bg-gray-100"
            >
              すべての自動作成ルールを表示
            </button>
          )}
        </div>
        <div className="flex space-x-3">
          {(isInline || !isMobile) && !hideCancel && (
            <button
              onClick={onClose}
              className="px-4 py-2 border rounded-md text-sm font-medium hover:bg-gray-100"
            >
              キャンセル
            </button>
          )}
          <button
            onClick={handleSaveClick}
            className="px-4 py-2 rounded-md text-sm font-medium text-white transition-colors"
            style={{ backgroundColor: colorHex, opacity: 0.9 }}
            onMouseOver={(e) => (e.currentTarget.style.opacity = "1")}
            onMouseOut={(e) => (e.currentTarget.style.opacity = "0.9")}
          >
            保存する
          </button>
        </div>
      </div>
    </>
  );

  if (isInline) {
    return formContent;
  }

  return (
    <div
      className={`fixed inset-0 bg-black/50 z-50 flex items-center justify-center ${
        isMobile ? "p-0" : "p-4"
      }`}
      onClick={onClose}
    >
      <div
        className={`bg-white flex flex-col shadow-xl ${
          isMobile ? "w-full h-full" : "rounded-lg w-full max-w-md max-h-[90vh]"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ヘッダー */}
        <div
          className={`flex items-center justify-between p-4 border-b sticky top-0 bg-white z-10 ${
            isMobile ? "" : "rounded-t-lg"
          }`}
        >
          <h3 className="text-lg font-bold">自動作成ルール設定</h3>
          <button
            onClick={onClose}
            className="p-1 rounded-full text-gray-500 hover:bg-gray-100"
            title="閉じる"
          >
            <CloseIcon />
          </button>
        </div>
        {formContent}
      </div>
    </div>
  );
}

// === 内部コンポーネント ===

// ダイアログ内のセクション見出し
const Section: React.FC<{
  title: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, children }) => (
  <div className="border-t pt-4 first:border-t-0 first:pt-0">
    <h4 className="text-sm font-semibold text-gray-700 mb-3">{title}</h4>
    <div className="space-y-3">{children}</div>
  </div>
);

// ダイアログ内のカスタムラジオボタン
const RadioOption: React.FC<{
  label: string;
  name: string;
  checked: boolean;
  onChange: () => void;
  color: string;
  children?: React.ReactNode;
}> = ({ label, name, checked, onChange, color, children }) => (
  <div className="flex items-center min-h-[32px]">
    <label className="flex items-center cursor-pointer">
      <input
        type="radio"
        name={name}
        checked={checked}
        onChange={onChange}
        className="hidden"
      />
      <div
        className={`w-4 h-4 rounded-full border-2 flex items-center justify-center mr-2 transition-all ${
          checked ? "" : "border-gray-400"
        }`}
        style={{ borderColor: checked ? color : undefined }}
      >
        {checked && (
          <div
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: color }}
          />
        )}
      </div>
      <span className="text-sm">{label}</span>
      {children && (
        <div className="ml-4 flex items-center gap-2">{children}</div>
      )}
    </label>
  </div>
);
